// Belgian number input, shared by every free-text number field of the costs
// feature: cost amounts, split amounts, tenant shares, meter readings,
// apportionment key weights and expected provisions. One parser, so
// "1.234,56" means the same thing in every field.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatNumber } from "@rentiohq/shared-frontend/dist/utils/number.utils";

export enum ENumberInputError {
  Invalid = "invalid",
  Negative = "negative",
  TooManyDecimals = "too_many_decimals",
}

export interface INumberInputResult {
  // Undefined when the field is empty or cannot be read as a number.
  value?: number;
  error?: ENumberInputError;
}

// Amounts are stored and billed in whole cents.
export const MONEY_DECIMALS = 2;
// A tenant share is a DECIMAL(5,2) column, so a third decimal is never kept.
export const PERCENTAGE_DECIMALS = 2;

const WEIGHT_DECIMALS = 2;

// Spaces (also the non-breaking ones a copied amount carries), the euro sign
// and a currency code are decoration, not part of the number.
const DECORATION = /[\s  €]|eur/gi;
// "2.000" or "1.250.000": dots grouping thousands. A leading zero ("0.500")
// is never a thousands group, so that one stays a decimal.
const DOT_THOUSANDS = /^[1-9]\d{0,2}(\.\d{3})+$/;
const COMMA_THOUSANDS = /^[1-9]\d{0,2}(,\d{3})+$/;
// A trailing decimal mark is allowed: "12," is what the field holds while the
// user is still typing "12,5".
const PLAIN_NUMBER = /^(\d+(\.\d*)?|\.\d+)$/;

const isGroupedInteger = (integerPart: string, groupMark: string): boolean =>
  !integerPart.includes(groupMark) ||
  new RegExp(`^\\d{1,3}(\\${groupMark}\\d{3})+$`).test(integerPart);

// Rewrites the typed text to "1234.56" (dot decimal, no grouping), or
// undefined when it is not a number at all.
const normalize = (unsigned: string): string | undefined => {
  const lastDot = unsigned.lastIndexOf(".");
  const lastComma = unsigned.lastIndexOf(",");

  // Both separators: the last one is the decimal mark ("1.234,56" and
  // "1,234.56"), the other one only groups thousands.
  if (lastDot !== -1 && lastComma !== -1) {
    const decimalMark = lastComma > lastDot ? "," : ".";
    const groupMark = decimalMark === "," ? "." : ",";
    const [integerPart, ...rest] = unsigned.split(decimalMark);
    if (rest.length !== 1 || !isGroupedInteger(integerPart, groupMark)) {
      return undefined;
    }
    return `${integerPart.split(groupMark).join("")}.${rest[0]}`;
  }

  if (lastComma !== -1) {
    // A Belgian decimal comma ("12,5"), or English grouping ("1,250,000").
    if (unsigned.indexOf(",") === lastComma) return unsigned.replace(",", ".");
    return COMMA_THOUSANDS.test(unsigned)
      ? unsigned.split(",").join("")
      : undefined;
  }

  // Dots only: Belgian thousands ("2.000", "1.250") or a decimal dot
  // ("12.50", "1234.56").
  if (DOT_THOUSANDS.test(unsigned)) return unsigned.split(".").join("");
  return unsigned;
};

// Reads a number the way a Belgian property manager types it. A minus sign is
// reported instead of being dropped: dropping it turned a credit of -50 into
// a charge of 50.
export const parseLocaleNumber = (
  raw: string,
  options: { maxDecimals?: number } = {},
): INumberInputResult => {
  const { maxDecimals } = options;
  const text = raw.replace(DECORATION, "");
  if (text === "") return {};

  const isNegative = text.startsWith("-");
  const normalized = normalize(isNegative ? text.slice(1) : text);
  const isNumber = normalized !== undefined && PLAIN_NUMBER.test(normalized);
  const magnitude = isNumber ? Number(normalized) : undefined;

  if (isNegative) {
    return {
      value: magnitude === undefined ? undefined : -magnitude,
      error: ENumberInputError.Negative,
    };
  }
  if (magnitude === undefined) return { error: ENumberInputError.Invalid };

  const decimals = normalized?.split(".")[1]?.length ?? 0;
  if (maxDecimals != null && decimals > maxDecimals) {
    return { value: magnitude, error: ENumberInputError.TooManyDecimals };
  }

  return { value: magnitude };
};

// The value a field holds for its form: nothing while the text is empty or
// still wrong, so a half-understood number never reaches a calculation.
export const acceptedNumber = (
  raw: string,
  options: { maxDecimals?: number } = {},
): number | undefined => {
  const { value, error } = parseLocaleNumber(raw, options);
  return error ? undefined : value;
};

// What a number field says about text it could not take as it is.
export const numberInputErrorText = (params: {
  error?: ENumberInputError;
  maxDecimals?: number;
  // What a minus sign means here; a money field explains that credit notes
  // are not supported.
  negativeMessage?: string;
}): string | undefined => {
  const { error, maxDecimals, negativeMessage } = params;
  switch (error) {
    case ENumberInputError.Negative:
      return negativeMessage ?? getLocalizedText("costs.input.negative");
    case ENumberInputError.TooManyDecimals:
      return getLocalizedText("costs.input.too_many_decimals", {
        decimals: String(maxDecimals),
      });
    case ENumberInputError.Invalid:
      return getLocalizedText("costs.input.invalid");
    default:
      return undefined;
  }
};

// How a field shows a number once the user leaves it, so they see what was
// understood: "1.234,56" for money, "80,5" for a weight or a reading. The
// output always reads back to the same number.
export const formatNumberInput = (
  value: number,
  options: { isMoney?: boolean } = {},
): string => formatNumber(value, !!options.isMoney);

// An apportionment weight in a ratio ("80,5 / 180,5"): Belgian notation like
// every other number in the feature, and rounded, so a sum of floats never
// leaks as 57.599999999999994.
export const formatWeight = (weight: number): string => {
  const factor = 10 ** WEIGHT_DECIMALS;
  return formatNumber(Math.round(weight * factor) / factor, false);
};
